import { useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  useProductListRejectedItems,
  useProductListIssues,
} from '@/hooks/useProductLists'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import {
  getProductListItems,
  type ProductListItemRead,
  type ProductListRead,
} from '@/api/productLists'
import type { ImportPreview } from '@/api/planDetail'
import {
  issueCountByRow,
  withoutUnchangedMismatches,
  buildBlockingEntries,
  buildCorrectionEntries,
  buildWarningEntries,
  downloadIssueReport,
  downloadRejectedRowsReport,
  pluralize,
} from './importReview/importReview'
import {
  GROUP_PAGE_SIZE,
  GroupPager,
  ImportIssueGroup,
  IssueEntryList,
  IssueEntryRow,
} from './importReview/ImportIssueGroup'
import { ImportOutcome } from './importReview/ImportOutcome'
import { ImportItemsTable } from './importReview/ImportItemsTable'

export const IMPORT_VERDICT_ID = 'import-verdict'
const REJECTED_EXPORT_PAGE_SIZE = 200

/** Plan import only: SKU matches and edit permissions against the target version. */
export interface ImportMatchCheck {
  data?: ImportPreview
  isChecking: boolean
  error: unknown
  retry: () => void
}

interface ImportResultsSummaryProps {
  productList: ProductListRead
  fileName?: string
  matchCheck?: ImportMatchCheck
  /** Notifies the parent (which owns the import action) whenever the
   * blocking-mismatch state changes, so it can gate the import. */
  onMismatchStatusChange?: (status: {
    hasMismatches: boolean
    isLoading: boolean
  }) => void
}

function RejectedRows({ productListId }: { productListId: string }) {
  const [page, setPage] = useState(1)
  const { data, isLoading } = useProductListRejectedItems(
    productListId,
    page,
    GROUP_PAGE_SIZE
  )
  if (isLoading) {
    return (
      <p className="flex items-center gap-2 px-4 py-3 text-[13px] text-[#556374]">
        <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
        Loading rows…
      </p>
    )
  }
  return (
    <>
      <ul>
        {(data?.data ?? []).map(item => (
          <IssueEntryRow key={item.id} label={`Row ${item.row_number}`}>
            {(item.errors ?? []).map((err, index) => (
              <p key={index}>
                <span className="font-medium">{err.column}</span>{' '}
                <span className="text-[#556374]">{err.message}</span>
              </p>
            ))}
          </IssueEntryRow>
        ))}
      </ul>
      <GroupPager
        page={page}
        total={data?.pagination.total ?? 0}
        onPageChange={setPage}
      />
    </>
  )
}

export function ImportResultsSummary({
  productList,
  fileName = productList.name,
  matchCheck,
  onMismatchStatusChange,
}: ImportResultsSummaryProps) {
  const {
    data: issues,
    isLoading: issuesLoading,
    error: issuesError,
    refetch: refetchIssues,
  } = useProductListIssues(productList.id, productList.valid_rows, true)

  const previewIssues = matchCheck?.data?.issues
  const mismatchItems = useMemo(
    () =>
      withoutUnchangedMismatches(
        issues?.mismatchItems ?? [],
        matchCheck?.data?.unchanged_mismatches
      ),
    [issues, matchCheck?.data?.unchanged_mismatches]
  )
  const blockingEntries = useMemo(
    () => buildBlockingEntries(mismatchItems, previewIssues ?? []),
    [mismatchItems, previewIssues]
  )
  const warningEntries = useMemo(
    () => buildWarningEntries(issues?.otherWarningItems ?? []),
    [issues]
  )
  const correctionEntries = useMemo(
    () => buildCorrectionEntries(issues?.correctionMappings ?? []),
    [issues]
  )
  const issuesToFixByRow = useMemo(
    () => issueCountByRow(blockingEntries),
    [blockingEntries]
  )
  const matchedRowNumbers = matchCheck?.data?.matched_row_numbers
  const matchedRows = useMemo(
    () => new Set(matchedRowNumbers ?? []),
    [matchedRowNumbers]
  )

  const hasMismatches = mismatchItems.length > 0
  useEffect(() => {
    onMismatchStatusChange?.({
      hasMismatches,
      isLoading: issuesLoading || !!issuesError,
    })
  }, [hasMismatches, issuesLoading, issuesError, onMismatchStatusChange])

  const [isDownloadingRejected, setIsDownloadingRejected] = useState(false)
  const handleDownloadRejected = async () => {
    setIsDownloadingRejected(true)
    try {
      const items: ProductListItemRead[] = []
      for (let page = 1; ; page++) {
        const response = await getProductListItems(
          productList.id,
          page,
          REJECTED_EXPORT_PAGE_SIZE,
          false
        )
        items.push(...response.data)
        if (!response.pagination.has_more || response.data.length === 0) break
      }
      await downloadRejectedRowsReport(fileName, items)
    } catch (error) {
      toast.error(
        getErrorMessage(error, 'Could not download the skipped rows.')
      )
    } finally {
      setIsDownloadingRejected(false)
    }
  }

  const isChecking = issuesLoading || !!matchCheck?.isChecking
  const checkError = issuesError
    ? 'Could not load the value validations.'
    : matchCheck?.error
      ? `Could not check matching SKUs. ${getErrorMessage(matchCheck.error, 'Please try again.')}`
      : null

  const validRows = productList.valid_rows
  const needsFix = issuesToFixByRow.size
  const update = matchCheck?.data
    ? matchedRowNumbers
      ? matchedRowNumbers.filter(row => !issuesToFixByRow.has(row)).length
      : matchCheck.data.matching_rows
    : 0
  const counts = {
    update,
    add: Math.max(0, validRows - needsFix - update),
    needsFix,
    rejected: productList.invalid_rows,
    skipped: productList.skipped_rows,
  }
  const truncatedNote = issues?.truncated
    ? ' Only the first 5,000 rows were checked.'
    : ''

  const retryChecks = () => {
    if (issuesError) void refetchIssues()
    if (matchCheck?.error) matchCheck.retry()
  }

  let verdict: React.ReactNode
  if (checkError) {
    verdict = (
      <Alert
        variant="destructive"
        id={IMPORT_VERDICT_ID}
        className="bg-red-50/60"
      >
        <AlertCircle aria-hidden="true" className="h-4 w-4" />
        <AlertTitle>We couldn&apos;t finish checking your file</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>{checkError} Retry before importing.</p>
          <Button variant="outline" size="sm" onClick={retryChecks}>
            <RefreshCw aria-hidden="true" />
            Retry check
          </Button>
        </AlertDescription>
      </Alert>
    )
  } else if (isChecking) {
    verdict = (
      <Alert role="status" id={IMPORT_VERDICT_ID}>
        <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
        <AlertTitle>Checking your file</AlertTitle>
        <AlertDescription className="text-[#556374]">
          Matching SKUs and checking values against the plan&apos;s options…
        </AlertDescription>
      </Alert>
    )
  } else if (blockingEntries.length > 0) {
    const readyRows = validRows - needsFix
    verdict = (
      <Alert
        variant="destructive"
        id={IMPORT_VERDICT_ID}
        className="bg-red-50/60"
      >
        <AlertCircle aria-hidden="true" className="h-4 w-4" />
        <AlertTitle className="text-[15px] leading-5">
          {pluralize(blockingEntries.length, 'issue')} to fix before importing
        </AlertTitle>
        <AlertDescription className="space-y-3 text-[#1C1C1C]">
          <p>
            {needsFix === 1
              ? "1 row can't be imported as it is."
              : `${needsFix.toLocaleString()} rows can't be imported as they are.`}{' '}
            Fix {blockingEntries.length === 1 ? 'it' : 'them'} in your file and
            upload it again.
            {readyRows > 0 &&
              ` The other ${pluralize(readyRows, 'row is', 'rows are')} ready.`}
            {truncatedNote}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="bg-white"
            onClick={() =>
              downloadIssueReport(fileName, blockingEntries).catch(() =>
                toast.error(
                  'Could not download the issue report. Please try again.'
                )
              )
            }
          >
            <Download aria-hidden="true" />
            Download issue report
          </Button>
        </AlertDescription>
      </Alert>
    )
  } else if (validRows === 0) {
    verdict = (
      <Alert
        variant="destructive"
        id={IMPORT_VERDICT_ID}
        className="bg-red-50/60"
      >
        <AlertCircle aria-hidden="true" className="h-4 w-4" />
        <AlertTitle>Nothing to import</AlertTitle>
        <AlertDescription className="text-[#1C1C1C]">
          None of the rows in this file can be imported. Check the rows below,
          fix them in your file and upload it again.
        </AlertDescription>
      </Alert>
    )
  } else {
    const affected = matchCheck?.data?.affected_lines ?? 0
    const outcome = [
      affected > 0 && `${pluralize(affected, 'existing item')} will be updated`,
      counts.add > 0 && `${pluralize(counts.add, 'new item')} will be added`,
    ]
      .filter(Boolean)
      .join(' and ')
    verdict = (
      <Alert variant="success" role="status" id={IMPORT_VERDICT_ID}>
        <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
        <AlertTitle className="text-[15px] leading-5">
          Ready to import {pluralize(validRows, 'item')}
        </AlertTitle>
        <AlertDescription className="text-[#1C1C1C]">
          {outcome && `${outcome}.`}
          {counts.rejected > 0 &&
            ` ${pluralize(counts.rejected, 'row')} will be skipped.`}
          {warningEntries.length > 0 &&
            ' Some values import as blank or rounded. Review them below.'}
          {truncatedNote}
        </AlertDescription>
      </Alert>
    )
  }

  const showDetails = !checkError && !isChecking
  const issueCount =
    blockingEntries.length +
    counts.rejected +
    warningEntries.length +
    correctionEntries.length

  const itemsTable = (
    <ImportItemsTable
      rows={issues?.rows ?? []}
      matchedRows={matchedRows}
      issuesToFixByRow={issuesToFixByRow}
      truncated={!!issues?.truncated}
    />
  )

  return (
    <div className="space-y-5">
      {verdict}
      {showDetails && (
        <>
          <ImportOutcome counts={counts} showUpdateNote={counts.update > 0} />
          {issueCount > 0 ? (
            <Tabs defaultValue="issues">
              <TabsList
                variant="line"
                className="w-full justify-start border-b border-[#E5E7EB]"
              >
                <TabsTrigger
                  value="issues"
                  className="flex-none cursor-pointer px-3"
                >
                  Issues ({issueCount.toLocaleString()})
                </TabsTrigger>
                <TabsTrigger
                  value="items"
                  className="flex-none cursor-pointer px-3"
                >
                  Items ({(issues?.rows.length ?? 0).toLocaleString()})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="issues" className="mt-3 space-y-2.5">
                {blockingEntries.length > 0 && (
                  <ImportIssueGroup
                    tone="critical"
                    title="Must fix"
                    description="These values block the import. Fix them in your file."
                    count={blockingEntries.length}
                    defaultOpen
                  >
                    <IssueEntryList entries={blockingEntries} />
                  </ImportIssueGroup>
                )}
                {counts.rejected > 0 && (
                  <ImportIssueGroup
                    tone="neutral"
                    title="Won't be imported"
                    description="These rows have errors and are skipped. They don't update or add items."
                    count={counts.rejected}
                    defaultOpen={blockingEntries.length === 0}
                    action={
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => void handleDownloadRejected()}
                        disabled={isDownloadingRejected}
                        aria-busy={isDownloadingRejected}
                      >
                        {isDownloadingRejected ? (
                          <Loader2
                            aria-hidden="true"
                            className="animate-spin"
                          />
                        ) : (
                          <Download aria-hidden="true" />
                        )}
                        Download Excel
                      </Button>
                    }
                  >
                    <RejectedRows productListId={productList.id} />
                  </ImportIssueGroup>
                )}
                {warningEntries.length > 0 && (
                  <ImportIssueGroup
                    tone="warning"
                    title="Imported with changes"
                    description="These values don't import exactly as written."
                    count={warningEntries.length}
                  >
                    <IssueEntryList entries={warningEntries} />
                  </ImportIssueGroup>
                )}
                {correctionEntries.length > 0 && (
                  <ImportIssueGroup
                    tone="info"
                    title="Auto-corrected"
                    description="Hierarchy values and vendor names matched to an existing record. Nothing to do."
                    count={correctionEntries.length}
                  >
                    <IssueEntryList entries={correctionEntries} />
                  </ImportIssueGroup>
                )}
              </TabsContent>
              <TabsContent value="items" className="mt-3">
                {itemsTable}
              </TabsContent>
            </Tabs>
          ) : (
            itemsTable
          )}
        </>
      )}
    </div>
  )
}
